import { useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Bell, LogOut, Sprout, Menu, X, Search, LayoutGrid } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { memberMenuFor, memberTabsFor, ROLE_LABELS } from "@/lib/navigation";
import MobileTabBar from "@/components/MobileTabBar";
import MemberNotifications from "@/components/MemberNotifications";

export default function MemberLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen grid place-items-center text-ink/60">Memuat…</div>;
  // Guard: wajib login untuk mengakses ruang anggota.
  if (!user) return <Navigate to="/masuk" replace />;

  // Menu disesuaikan role (ANGGOTA vs MITRA_GERAI).
  const nav = memberMenuFor(user.role);
  const initials = (user.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  const firstName = (user.name || "").split(" ")[0];
  const doLogout = () => { logout(); navigate("/masuk"); };

  // Navigasi bawah (mobile): 4 menu utama + "Menu" yang membuka laci berisi semua menu.
  const tabs = [
    ...memberTabsFor(user.role).map((n) => ({ to: n.to, end: n.end, label: n.short || n.label, icon: n.icon })),
    { label: "Menu", icon: LayoutGrid, onClick: () => setSidebarOpen(true), active: sidebarOpen },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Latar gelap saat laci menu terbuka (mobile) */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-[45] bg-ink/40 backdrop-blur-[2px] animate-fade-in" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[82vw] max-w-72 lg:w-72 bg-white border-r border-stone-soft pt-safe pb-safe flex flex-col transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-300`}>
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-forest text-white grid place-items-center">
              <Sprout size={18} />
            </span>
            <div className="leading-tight">
              <div className="font-display font-bold text-base">Koperasi Andalan Dapur Nusantara</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink/55">Ruang Anggota</div>
            </div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded hover:bg-stone-soft/40">
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-2">
          <div className="rounded-2xl bg-cream p-4 flex items-center gap-3 border border-stone-soft">
            <Avatar className="w-11 h-11 ring-2 ring-amber-warm/40">
              <AvatarFallback className="bg-forest/10 text-forest font-bold text-sm">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate" data-testid="member-name">{user.name}</div>
              <div className="text-xs text-ink/55 truncate">{ROLE_LABELS[user.role] || user.role} · {user.koperasi_member_number || "-"}</div>
            </div>
          </div>
        </div>

        <nav className="px-3 py-2 space-y-1 flex-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setSidebarOpen(false)}
              data-testid={`member-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-forest text-white shadow-sm" : "text-ink/75 hover:bg-cream hover:text-ink"
                }`
              }
            >
              <n.icon size={17} />
              <span className="font-medium flex-1">{n.label}</span>
              {n.badge && <span className="text-[9px] uppercase tracking-wider bg-amber-warm/25 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{n.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-stone-soft lg:border-0">
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-ink/65 hover:bg-cream hover:text-ink transition" data-testid="member-logout">
            <LogOut size={17} />
            <span className="font-medium">Keluar</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-stone-soft pt-safe">
          <div className="px-4 lg:px-10 h-14 lg:h-16 flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="hidden md:block lg:hidden p-2 rounded-lg hover:bg-white" data-testid="member-sidebar-toggle">
              <Menu size={20} />
            </button>
            {/* Mobile: sapaan + avatar, gaya aplikasi */}
            <Link to="/anggota/pengaturan" className="md:hidden flex items-center gap-2.5 flex-1 min-w-0" data-testid="member-mobile-profile">
              <Avatar className="w-9 h-9 ring-2 ring-amber-warm/40">
                <AvatarFallback className="bg-forest text-white font-bold text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="leading-tight min-w-0">
                <div className="text-[11px] text-ink/55">Halo,</div>
                <div className="text-sm font-semibold text-ink truncate">{firstName}</div>
              </div>
            </Link>
            <div className="hidden md:block flex-1 max-w-xl relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/55" />
              <Input
                placeholder="Cari transaksi, pesanan, produk..."
                className="pl-10 bg-white border-stone-soft rounded-full"
                data-testid="member-search-input"
              />
            </div>
            <button className="relative w-10 h-10 rounded-full bg-white border border-stone-soft grid place-items-center hover:bg-cream transition" data-testid="member-notif-button">
              <Bell size={17} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-terracotta rounded-full"></span>
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-5 lg:px-10 py-5 lg:py-8 pb-tabbar">
          <div className="pb-6 lg:pb-0"><Outlet /></div>
        </main>
      </div>

      <MobileTabBar items={tabs} testIdPrefix="member-tab" />

      {/* Floating button informasi terbaru (pesanan masuk, verifikasi, dsb.) */}
      <MemberNotifications />
    </div>
  );
}
